import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, ShoppingCart, FileText, Upload, Download, RefreshCw, 
  CheckCircle, XCircle, AlertTriangle, Clock, Search, Filter, Play, Eye,
  FileSpreadsheet, TrendingUp, AlertCircle, Edit, Plus, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const OrderManagementDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Dashboard summary
  const [summary, setSummary] = useState(null);
  
  // Customers
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerPage, setCustomerPage] = useState(1);
  const [customerTotal, setCustomerTotal] = useState(0);
  
  // Sales Orders
  const [orders, setOrders] = useState([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderFilter, setOrderFilter] = useState('pending');
  const [orderPage, setOrderPage] = useState(1);
  const [orderTotal, setOrderTotal] = useState(0);
  
  // Auto Pick Slips
  const [autoSlips, setAutoSlips] = useState([]);
  const [selectedSlips, setSelectedSlips] = useState([]);
  
  // Import dialogs
  const [customerImportOpen, setCustomerImportOpen] = useState(false);
  const [orderImportOpen, setOrderImportOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  
  // Generate dialog
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateDays, setGenerateDays] = useState(2);
  const [generateDryRun, setGenerateDryRun] = useState(true);
  const [generateResult, setGenerateResult] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Reports dialog
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Slip detail dialog (Phase 2)
  const [slipDetailOpen, setSlipDetailOpen] = useState(false);
  const [selectedSlipDetail, setSelectedSlipDetail] = useState(null);
  const [slipDetailLoading, setSlipDetailLoading] = useState(false);

  // Manual create dialog (Phase 3)
  const [manualCreateOpen, setManualCreateOpen] = useState(false);
  const [manualCustomerCode, setManualCustomerCode] = useState('');
  const [manualOrderNumbers, setManualOrderNumbers] = useState('');
  const [manualRemarks, setManualRemarks] = useState('');
  const [manualCreating, setManualCreating] = useState(false);

  // Pickers for auto-assign
  const [pickers, setPickers] = useState([]);
  const [selectedPicker, setSelectedPicker] = useState('');

  const fetchSummary = useCallback(async () => {
    try {
      const response = await apiClient.get('/order-management/dashboard/summary');
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: customerPage,
        page_size: 20
      });
      if (customerSearch) params.append('search', customerSearch);
      
      const response = await apiClient.get(`/order-management/customer-master?${params}`);
      setCustomers(response.data.customers || []);
      setCustomerTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, [customerPage, customerSearch]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: orderPage,
        page_size: 20
      });
      if (orderSearch) params.append('search', orderSearch);
      if (orderFilter === 'pending') params.append('pending_only', 'true');
      else if (orderFilter !== 'all') params.append('status', orderFilter);
      
      const response = await apiClient.get(`/order-management/sales-orders?${params}`);
      setOrders(response.data.orders || []);
      setOrderTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [orderPage, orderSearch, orderFilter]);

  const fetchAutoSlips = useCallback(async () => {
    try {
      const response = await apiClient.get('/order-management/auto-pick-slips?status=PENDING_APPROVAL');
      setAutoSlips(response.data.pick_slips || []);
    } catch (error) {
      console.error('Error fetching auto slips:', error);
    }
  }, []);

  const fetchPickers = useCallback(async () => {
    try {
      const response = await apiClient.get('/staff?section=FG%20Picking');
      setPickers(response.data.staff || []);
    } catch (error) {
      console.error('Error fetching pickers:', error);
    }
  }, []);

  const fetchSlipDetail = async (slipId) => {
    setSlipDetailLoading(true);
    try {
      const response = await apiClient.get(`/order-management/auto-pick-slips/${slipId}`);
      setSelectedSlipDetail(response.data);
      setSlipDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load slip details');
    } finally {
      setSlipDetailLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchAutoSlips(), fetchPickers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchAutoSlips, fetchPickers]);

  useEffect(() => {
    if (activeTab === 'customers') fetchCustomers();
  }, [activeTab, fetchCustomers]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
  }, [activeTab, fetchOrders]);

  // CSV Import handlers
  const handleImport = async (type, file) => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    
    const endpoint = type === 'customer' 
      ? `/order-management/customer-master/import?replace_existing=${replaceExisting}`
      : `/order-management/sales-orders/import?replace_existing=${replaceExisting}`;

    try {
      setImportProgress({ phase: 'starting', percentage: 0 });
      
      const response = await fetch(`${apiClient.defaults.baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: formData
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n').filter(line => line.startsWith('data: '));

        for (const line of lines) {
          try {
            const data = JSON.parse(line.replace('data: ', ''));
            if (data.error) {
              toast.error(data.error);
              setImportProgress(null);
              return;
            }
            setImportProgress(data);
            
            if (data.phase === 'completed') {
              const message = type === 'customer'
                ? `Imported ${data.imported} customers, ${data.skipped} updated`
                : `Imported ${data.orders_imported} orders, ${data.orders_updated} updated`;
              toast.success(message);
              
              if (data.billable_reset) {
                toast.info('Customer billable consumed amounts have been reset');
              }
              
              // Refresh data
              fetchSummary();
              if (type === 'customer') fetchCustomers();
              else fetchOrders();
              
              setTimeout(() => {
                setImportProgress(null);
                setCustomerImportOpen(false);
                setOrderImportOpen(false);
                setReplaceExisting(false);
              }, 1500);
            }
          } catch (e) {
            console.error('Parse error:', e);
          }
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed');
      setImportProgress(null);
    }
  };

  // Generate pick slips
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerateResult(null);
    
    try {
      const response = await apiClient.post('/order-management/generate-pick-slips', {
        dispatch_date_range_days: generateDays,
        dry_run: generateDryRun
      });
      
      setGenerateResult(response.data);
      
      if (!generateDryRun && response.data.generated_count > 0) {
        toast.success(`Generated ${response.data.generated_count} pick slip(s)`);
        fetchAutoSlips();
        fetchSummary();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // Approve/Reject slips
  const handleApproveSlips = async (approved) => {
    if (selectedSlips.length === 0) {
      toast.error('Please select at least one slip');
      return;
    }

    try {
      const response = await apiClient.post('/order-management/auto-pick-slips/approve', {
        pick_slip_ids: selectedSlips,
        approved,
        auto_assign_picker: approved && selectedPicker ? selectedPicker : null
      });

      if (approved) {
        toast.success(`Approved ${response.data.approved_count} slip(s)${selectedPicker ? ' and assigned to picker' : ''}`);
      } else {
        toast.success(`Rejected ${response.data.rejected_count} slip(s)`);
      }

      setSelectedSlips([]);
      setSelectedPicker('');
      fetchAutoSlips();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Manual create pick slip
  const handleManualCreate = async () => {
    if (!manualCustomerCode || !manualOrderNumbers) {
      toast.error('Please fill in customer code and order numbers');
      return;
    }

    setManualCreating(true);
    try {
      const orderList = manualOrderNumbers.split(',').map(o => o.trim()).filter(o => o);
      
      const response = await apiClient.post('/order-management/pick-slips/manual-create', {
        customer_code: manualCustomerCode.trim(),
        order_numbers: orderList,
        remarks: manualRemarks
      });

      toast.success(`Created pick slip: ${response.data.pick_slip_number}`);
      setManualCreateOpen(false);
      setManualCustomerCode('');
      setManualOrderNumbers('');
      setManualRemarks('');
      fetchSummary();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pick slip');
    } finally {
      setManualCreating(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async (type) => {
    try {
      const endpoint = type === 'customer'
        ? '/order-management/customer-master/template/download'
        : '/order-management/sales-orders/template/download';
      
      const response = await apiClient.get(endpoint, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', type === 'customer' ? 'customer_master_template.csv' : 'sales_orders_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Reports
  const handleLoadReport = async (reportType) => {
    setActiveReport(reportType);
    setReportLoading(true);
    setReportData(null);

    try {
      let endpoint = '';
      switch (reportType) {
        case 'no-stock':
          endpoint = '/order-management/reports/designs-not-in-stock';
          break;
        case 'late-dispatch':
          endpoint = '/order-management/reports/late-dispatch-orders';
          break;
        case 'credit-status':
          endpoint = '/order-management/reports/customer-credit-status';
          break;
        case 'generation-history':
          endpoint = '/order-management/reports/generation-history?days=30';
          break;
        case 'customer-summary':
          endpoint = '/order-management/reports/customer-order-summary';
          break;
        case 'picker-workload':
          endpoint = '/order-management/reports/picker-workload';
          break;
        default:
          return;
      }

      const response = await apiClient.get(endpoint);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setReportLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      VIP: 'bg-purple-100 text-purple-700',
      GOLD: 'bg-yellow-100 text-yellow-700',
      STANDARD: 'bg-gray-100 text-gray-700'
    };
    return <Badge className={colors[tier] || colors.STANDARD}>{tier}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      APPROVED: 'bg-green-100 text-green-700',
      PENDING: 'bg-yellow-100 text-yellow-700',
      CANCELLED: 'bg-red-100 text-red-700',
      ACTIVE: 'bg-green-100 text-green-700',
      INACTIVE: 'bg-gray-100 text-gray-700',
      BLOCKED: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="order-mgmt-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <ShoppingCart className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold font-display">Order Management</h1>
                <p className="text-sm text-[#E8DCC8]">Auto Pick Slip Generation</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setManualCreateOpen(true)}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="manual-create-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Manual Slip
            </Button>
            <Button
              variant="outline"
              onClick={() => setReportsDialogOpen(true)}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="order-mgmt-reports-btn"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Reports
            </Button>
            <Button
              onClick={() => setGenerateDialogOpen(true)}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="order-mgmt-generate-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Generate Pick Slips
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E]">Active Customers</p>
                  <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.customers?.active || 0}</p>
                </div>
                <Users className="w-10 h-10 text-blue-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E]">Pending Orders</p>
                  <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.orders?.pending || 0}</p>
                </div>
                <ShoppingCart className="w-10 h-10 text-amber-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E]">Due Within 2 Days</p>
                  <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.orders?.due_within_2_days || 0}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
            </div>
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5F6E]">Pending Approval</p>
                  <p className="text-3xl font-bold text-[#1A1F2E] mt-1">{summary.pick_slips?.pending_approval || 0}</p>
                </div>
                <Clock className="w-10 h-10 text-purple-500" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-[#E5DFD0]">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Sales Orders</TabsTrigger>
            <TabsTrigger value="approval" data-testid="tab-approval">
              Pending Approval
              {autoSlips.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{autoSlips.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Import Cards */}
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4">Customer Master</h3>
                <p className="text-sm text-[#5A5F6E] mb-4">
                  Import customer data including credit limits and tiers.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadTemplate('customer')}
                    data-testid="download-customer-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    onClick={() => setCustomerImportOpen(true)}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                    data-testid="import-customer-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4">Sales Orders</h3>
                <p className="text-sm text-[#5A5F6E] mb-4">
                  Import sales orders with items, quantities, and dispatch dates.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadTemplate('order')}
                    data-testid="download-order-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    onClick={() => setOrderImportOpen(true)}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                    data-testid="import-order-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 md:col-span-2">
                <h3 className="text-lg font-semibold text-[#1A1F2E] mb-4">Auto Pick Slip Generation</h3>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-800 mb-2">Business Rules Applied:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Only APPROVED orders from ACTIVE customers</li>
                    <li>• Dispatch date within configured days (default: 2 days)</li>
                    <li>• Customer credit limit validation</li>
                    <li>• Stock availability check in FG inventory</li>
                    <li>• One consolidated slip per customer</li>
                    <li>• Priority: Manual Override → VIP Tier → GOLD Tier → FIFO</li>
                  </ul>
                </div>
                <Button
                  onClick={() => setGenerateDialogOpen(true)}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  data-testid="generate-slips-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Generate Pick Slips
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="bg-white rounded-lg border border-[#E5DFD0]">
              <div className="p-4 border-b border-[#E5DFD0] flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      value={customerSearch}
                      onChange={(e) => {
                        setCustomerSearch(e.target.value);
                        setCustomerPage(1);
                      }}
                      placeholder="Search customers..."
                      className="pl-10 w-64"
                      data-testid="customer-search-input"
                    />
                  </div>
                  <span className="text-sm text-[#5A5F6E]">{customerTotal} customers</span>
                </div>
                <Button
                  onClick={() => setCustomerImportOpen(true)}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Code</th>
                      <th className="px-4 py-3 text-left font-medium">Name</th>
                      <th className="px-4 py-3 text-center font-medium">Tier</th>
                      <th className="px-4 py-3 text-center font-medium">Status</th>
                      <th className="px-4 py-3 text-right font-medium">Credit Limit</th>
                      <th className="px-4 py-3 text-right font-medium">Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => (
                      <tr key={customer.customer_code} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono">{customer.customer_code}</td>
                        <td className="px-4 py-3">{customer.customer_name}</td>
                        <td className="px-4 py-3 text-center">{getTierBadge(customer.tier)}</td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(customer.status)}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          ₹{(customer.credit_limit || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          <span className={customer.available_credit < 0 ? 'text-red-600' : 'text-green-600'}>
                            ₹{(customer.available_credit || 0).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {customers.length === 0 && (
                <div className="p-8 text-center text-[#5A5F6E]">
                  No customers found. Import customer master to get started.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="bg-white rounded-lg border border-[#E5DFD0]">
              <div className="p-4 border-b border-[#E5DFD0] flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      value={orderSearch}
                      onChange={(e) => {
                        setOrderSearch(e.target.value);
                        setOrderPage(1);
                      }}
                      placeholder="Search orders..."
                      className="pl-10 w-64"
                      data-testid="order-search-input"
                    />
                  </div>
                  <Select value={orderFilter} onValueChange={(v) => { setOrderFilter(v); setOrderPage(1); }}>
                    <SelectTrigger className="w-40" data-testid="order-filter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending Slips</SelectItem>
                      <SelectItem value="all">All Orders</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-[#5A5F6E]">{orderTotal} orders</span>
                </div>
                <Button
                  onClick={() => setOrderImportOpen(true)}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Order #</th>
                      <th className="px-4 py-3 text-left font-medium">Customer</th>
                      <th className="px-4 py-3 text-center font-medium">Items</th>
                      <th className="px-4 py-3 text-right font-medium">Qty</th>
                      <th className="px-4 py-3 text-right font-medium">Value</th>
                      <th className="px-4 py-3 text-center font-medium">Dispatch</th>
                      <th className="px-4 py-3 text-center font-medium">Status</th>
                      <th className="px-4 py-3 text-center font-medium">Slip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_number} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono">{order.order_number}</td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-medium">{order.customer_name || order.customer_code}</span>
                            {order.customer_tier && (
                              <span className="ml-2">{getTierBadge(order.customer_tier)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">{order.items?.length || 0}</td>
                        <td className="px-4 py-3 text-right">{order.total_qty?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">₹{(order.total_value || 0).toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">{order.dispatch_date}</td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(order.order_status)}</td>
                        <td className="px-4 py-3 text-center">
                          {order.pick_slip_generated ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <Clock className="w-5 h-5 text-gray-400 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {orders.length === 0 && (
                <div className="p-8 text-center text-[#5A5F6E]">
                  No orders found. Import sales orders to get started.
                </div>
              )}
            </div>
          </TabsContent>

          {/* Pending Approval Tab */}
          <TabsContent value="approval">
            <div className="bg-white rounded-lg border border-[#E5DFD0]">
              <div className="p-4 border-b border-[#E5DFD0] flex flex-wrap items-center justify-between gap-4">
                <h3 className="font-semibold text-[#1A1F2E]">Auto-Generated Pick Slips Pending Approval</h3>
                <div className="flex flex-wrap gap-2 items-center">
                  {selectedSlips.length > 0 && (
                    <div className="flex items-center gap-2 mr-4">
                      <Label className="text-sm whitespace-nowrap">Auto-assign to:</Label>
                      <Select value={selectedPicker} onValueChange={setSelectedPicker}>
                        <SelectTrigger className="w-40" data-testid="picker-select">
                          <SelectValue placeholder="Select picker" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">No assignment</SelectItem>
                          {pickers.map(picker => (
                            <SelectItem key={picker.staff_id} value={picker.staff_id}>
                              {picker.staff_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => handleApproveSlips(false)}
                    disabled={selectedSlips.length === 0}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="reject-slips-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleApproveSlips(true)}
                    disabled={selectedSlips.length === 0}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                    data-testid="approve-slips-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                </div>
              </div>
              
              {autoSlips.length === 0 ? (
                <div className="p-8 text-center text-[#5A5F6E]">
                  No pick slips pending approval.
                </div>
              ) : (
                <div className="divide-y divide-[#E5DFD0]">
                  {autoSlips.map((slip) => (
                    <div key={slip.slip_id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start gap-4">
                        <Checkbox
                          checked={selectedSlips.includes(slip.slip_id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSlips([...selectedSlips, slip.slip_id]);
                            } else {
                              setSelectedSlips(selectedSlips.filter(id => id !== slip.slip_id));
                            }
                          }}
                          data-testid={`slip-checkbox-${slip.slip_id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-[#1A1F2E]">{slip.pick_slip_number}</span>
                              <span className="text-[#5A5F6E]">{slip.customer_name}</span>
                              {slip.customer_tier && getTierBadge(slip.customer_tier)}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => fetchSlipDetail(slip.slip_id)}
                                className="ml-2"
                                data-testid={`view-slip-${slip.slip_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="text-sm text-[#5A5F6E]">
                              {new Date(slip.generated_at).toLocaleString()}
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-[#5A5F6E]">Orders:</span>
                              <span className="ml-2 font-medium">{slip.source_orders?.length || 0}</span>
                            </div>
                            <div>
                              <span className="text-[#5A5F6E]">Items:</span>
                              <span className="ml-2 font-medium">{slip.total_items}</span>
                            </div>
                            <div>
                              <span className="text-[#5A5F6E]">Qty:</span>
                              <span className="ml-2 font-medium">{slip.total_qty?.toLocaleString()}</span>
                            </div>
                            <div>
                              <span className="text-[#5A5F6E]">Value:</span>
                              <span className="ml-2 font-medium">₹{(slip.total_value || 0).toLocaleString()}</span>
                            </div>
                          </div>
                          {slip.items_no_stock?.length > 0 && (
                            <div className="mt-2 text-sm text-amber-600">
                              <AlertTriangle className="w-4 h-4 inline mr-1" />
                              {slip.items_no_stock.length} item(s) excluded due to no stock
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Customer Import Dialog */}
      <Dialog open={customerImportOpen} onOpenChange={setCustomerImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customer Master</DialogTitle>
            <DialogDescription>
              Upload a CSV file with customer data including credit limits.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="replace-customers" 
                checked={replaceExisting}
                onCheckedChange={setReplaceExisting}
                data-testid="replace-customers-checkbox"
              />
              <Label htmlFor="replace-customers" className="text-sm">
                Replace existing data (will reset billable consumed amounts)
              </Label>
            </div>
            
            {replaceExisting && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                Warning: This will delete all existing customers and reset consumed billable amounts.
              </div>
            )}
            
            <div>
              <Label>Select CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => handleImport('customer', e.target.files[0])}
                className="mt-2"
                disabled={importProgress !== null}
                data-testid="customer-file-input"
              />
            </div>
            
            {importProgress && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{importProgress.phase}</span>
                  <span className="text-sm text-[#5A5F6E]">{importProgress.percentage || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#2D5F4F] h-2 rounded-full transition-all" 
                    style={{ width: `${importProgress.percentage || 0}%` }}
                  />
                </div>
                {importProgress.message && (
                  <p className="text-xs text-[#5A5F6E] mt-2">{importProgress.message}</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownloadTemplate('customer')}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Import Dialog */}
      <Dialog open={orderImportOpen} onOpenChange={setOrderImportOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Sales Orders</DialogTitle>
            <DialogDescription>
              Upload a CSV file with sales order data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="replace-orders" 
                checked={replaceExisting}
                onCheckedChange={setReplaceExisting}
                data-testid="replace-orders-checkbox"
              />
              <Label htmlFor="replace-orders" className="text-sm">
                Replace existing orders
              </Label>
            </div>
            
            <div>
              <Label>Select CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => handleImport('order', e.target.files[0])}
                className="mt-2"
                disabled={importProgress !== null}
                data-testid="order-file-input"
              />
            </div>
            
            {importProgress && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{importProgress.phase}</span>
                  <span className="text-sm text-[#5A5F6E]">{importProgress.percentage || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#2D5F4F] h-2 rounded-full transition-all" 
                    style={{ width: `${importProgress.percentage || 0}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownloadTemplate('order')}>
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generate Auto Pick Slips</DialogTitle>
            <DialogDescription>
              Generate pick slips from pending sales orders based on business rules.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dispatch Date Range (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={generateDays}
                  onChange={(e) => setGenerateDays(parseInt(e.target.value) || 2)}
                  className="mt-2"
                  data-testid="generate-days-input"
                />
                <p className="text-xs text-[#5A5F6E] mt-1">
                  Generate slips for orders due within this many days
                </p>
              </div>
              <div>
                <Label>Mode</Label>
                <div className="mt-2 flex items-center space-x-2">
                  <Checkbox
                    id="dry-run"
                    checked={generateDryRun}
                    onCheckedChange={setGenerateDryRun}
                    data-testid="dry-run-checkbox"
                  />
                  <Label htmlFor="dry-run" className="text-sm font-normal">
                    Preview only (don't create slips)
                  </Label>
                </div>
              </div>
            </div>
            
            {generateResult && (
              <div className="border border-[#E5DFD0] rounded-lg">
                <div className="p-4 bg-gray-50 border-b border-[#E5DFD0]">
                  <h4 className="font-medium">
                    {generateResult.dry_run ? 'Preview' : 'Generation'} Results
                  </h4>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-[#5A5F6E]">Slips Generated:</span>
                      <span className="ml-2 font-medium text-green-600">{generateResult.generated_count}</span>
                    </div>
                    <div>
                      <span className="text-[#5A5F6E]">Customers Skipped:</span>
                      <span className="ml-2 font-medium text-amber-600">{generateResult.skipped_count}</span>
                    </div>
                  </div>
                  
                  {generateResult.generated_slips?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">Generated Slips:</p>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {generateResult.generated_slips.map((slip, idx) => (
                          <div key={idx} className="text-sm bg-green-50 p-2 rounded">
                            <span className="font-mono">{slip.slip_number}</span>
                            <span className="mx-2">-</span>
                            <span>{slip.customer_name}</span>
                            <span className="mx-2">|</span>
                            <span>{slip.items_count} items, {slip.total_qty} qty</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {generateResult.skipped_reasons?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">Skipped Reasons:</p>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {generateResult.skipped_reasons.map((reason, idx) => (
                          <div key={idx} className="text-sm bg-amber-50 p-2 rounded">
                            <span className="font-medium">{reason.customer_code}</span>
                            {reason.customer_name && <span> ({reason.customer_name})</span>}
                            <span className="mx-2">-</span>
                            <span className="text-amber-700">{reason.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="run-generate-btn"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {generateDryRun ? 'Preview' : 'Generate'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reports Dialog */}
      <Dialog open={reportsDialogOpen} onOpenChange={setReportsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Management Reports</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                variant={activeReport === 'no-stock' ? 'default' : 'outline'}
                onClick={() => handleLoadReport('no-stock')}
                className={activeReport === 'no-stock' ? 'bg-[#2D5F4F]' : ''}
                size="sm"
                data-testid="report-no-stock-btn"
              >
                No Stock
              </Button>
              <Button
                variant={activeReport === 'late-dispatch' ? 'default' : 'outline'}
                onClick={() => handleLoadReport('late-dispatch')}
                className={activeReport === 'late-dispatch' ? 'bg-[#2D5F4F]' : ''}
                size="sm"
                data-testid="report-late-dispatch-btn"
              >
                Late Dispatch
              </Button>
              <Button
                variant={activeReport === 'credit-status' ? 'default' : 'outline'}
                onClick={() => handleLoadReport('credit-status')}
                className={activeReport === 'credit-status' ? 'bg-[#2D5F4F]' : ''}
                size="sm"
                data-testid="report-credit-status-btn"
              >
                Credit Status
              </Button>
              <Button
                variant={activeReport === 'generation-history' ? 'default' : 'outline'}
                onClick={() => handleLoadReport('generation-history')}
                className={activeReport === 'generation-history' ? 'bg-[#2D5F4F]' : ''}
                size="sm"
                data-testid="report-gen-history-btn"
              >
                <History className="w-3 h-3 mr-1" />
                Gen. History
              </Button>
              <Button
                variant={activeReport === 'customer-summary' ? 'default' : 'outline'}
                onClick={() => handleLoadReport('customer-summary')}
                className={activeReport === 'customer-summary' ? 'bg-[#2D5F4F]' : ''}
                size="sm"
                data-testid="report-cust-summary-btn"
              >
                Order Summary
              </Button>
              <Button
                variant={activeReport === 'picker-workload' ? 'default' : 'outline'}
                onClick={() => handleLoadReport('picker-workload')}
                className={activeReport === 'picker-workload' ? 'bg-[#2D5F4F]' : ''}
                size="sm"
                data-testid="report-picker-workload-btn"
              >
                Picker Workload
              </Button>
            </div>
            
            {reportLoading && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-[#2D5F4F]" />
                <span className="ml-2">Loading report...</span>
              </div>
            )}
            
            {!reportLoading && reportData && activeReport === 'no-stock' && (
              <div>
                <p className="text-sm text-[#5A5F6E] mb-4">{reportData.total} items with insufficient stock</p>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Barcode</th>
                        <th className="px-3 py-2 text-left">Design</th>
                        <th className="px-3 py-2 text-right">Required</th>
                        <th className="px-3 py-2 text-right">Available</th>
                        <th className="px-3 py-2 text-right">Shortfall</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.items?.map((item, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2 font-mono">{item.barcode}</td>
                          <td className="px-3 py-2">{item.design_name}</td>
                          <td className="px-3 py-2 text-right">{item.required_qty}</td>
                          <td className="px-3 py-2 text-right">{item.available_qty}</td>
                          <td className="px-3 py-2 text-right text-red-600 font-medium">{item.shortfall}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {!reportLoading && reportData && activeReport === 'late-dispatch' && (
              <div>
                <p className="text-sm text-[#5A5F6E] mb-4">{reportData.total} overdue orders</p>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Order #</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-center">Dispatch Date</th>
                        <th className="px-3 py-2 text-center">Days Overdue</th>
                        <th className="px-3 py-2 text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.orders?.map((order, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2 font-mono">{order.order_number}</td>
                          <td className="px-3 py-2">{order.customer_name || order.customer_code}</td>
                          <td className="px-3 py-2 text-center">{order.dispatch_date}</td>
                          <td className="px-3 py-2 text-center">
                            <Badge className="bg-red-100 text-red-700">{order.days_overdue} days</Badge>
                          </td>
                          <td className="px-3 py-2 text-right">₹{(order.total_value || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {!reportLoading && reportData && activeReport === 'credit-status' && (
              <div>
                <p className="text-sm text-[#5A5F6E] mb-4">{reportData.total} customers with pending orders</p>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-center">Tier</th>
                        <th className="px-3 py-2 text-right">Credit Limit</th>
                        <th className="px-3 py-2 text-right">Available</th>
                        <th className="px-3 py-2 text-right">Pending Value</th>
                        <th className="px-3 py-2 text-center">Can Fulfill?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.customers?.map((customer, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2">
                            <div className="font-medium">{customer.customer_name}</div>
                            <div className="text-xs text-[#5A5F6E]">{customer.customer_code}</div>
                          </td>
                          <td className="px-3 py-2 text-center">{getTierBadge(customer.tier)}</td>
                          <td className="px-3 py-2 text-right">₹{(customer.credit_limit || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">
                            <span className={customer.available_credit < 0 ? 'text-red-600' : ''}>
                              ₹{(customer.available_credit || 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">₹{(customer.pending_value || 0).toLocaleString()}</td>
                          <td className="px-3 py-2 text-center">
                            {customer.can_fulfill ? (
                              <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {!reportLoading && !reportData && activeReport && (
              <div className="text-center py-8 text-[#5A5F6E]">
                Select a report to view
              </div>
            )}

            {/* Generation History Report */}
            {!reportLoading && reportData && activeReport === 'generation-history' && (
              <div>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <span className="text-sm text-[#5A5F6E]">Pending</span>
                    <p className="text-xl font-bold text-amber-600">{reportData.status_breakdown?.PENDING_APPROVAL || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <span className="text-sm text-[#5A5F6E]">Approved</span>
                    <p className="text-xl font-bold text-green-600">{reportData.status_breakdown?.APPROVED || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded">
                    <span className="text-sm text-[#5A5F6E]">Rejected</span>
                    <p className="text-xl font-bold text-red-600">{reportData.status_breakdown?.REJECTED || 0}</p>
                  </div>
                </div>
                <p className="text-sm text-[#5A5F6E] mb-2">Generated {reportData.total_generated} slips in last {reportData.period_days} days</p>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Date</th>
                        <th className="px-3 py-2 text-right">Count</th>
                        <th className="px-3 py-2 text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.by_date?.map((row, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2">{row.date}</td>
                          <td className="px-3 py-2 text-right">{row.count}</td>
                          <td className="px-3 py-2 text-right">₹{(row.total_value || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Customer Order Summary Report */}
            {!reportLoading && reportData && activeReport === 'customer-summary' && (
              <div>
                <p className="text-sm text-[#5A5F6E] mb-4">{reportData.total_customers} customers with orders</p>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-center">Total</th>
                        <th className="px-3 py-2 text-center">Pending</th>
                        <th className="px-3 py-2 text-center">Fulfilled</th>
                        <th className="px-3 py-2 text-right">Rate</th>
                        <th className="px-3 py-2 text-right">Pending Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.customers?.map((c, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2">
                            <div className="font-medium">{c.customer_name}</div>
                            <div className="text-xs text-[#5A5F6E]">{c.customer_code}</div>
                          </td>
                          <td className="px-3 py-2 text-center">{c.total_orders}</td>
                          <td className="px-3 py-2 text-center text-amber-600">{c.pending_orders}</td>
                          <td className="px-3 py-2 text-center text-green-600">{c.fulfilled_orders}</td>
                          <td className="px-3 py-2 text-right">{c.fulfillment_rate}%</td>
                          <td className="px-3 py-2 text-right">₹{(c.pending_value || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Picker Workload Report */}
            {!reportLoading && reportData && activeReport === 'picker-workload' && (
              <div>
                <div className="bg-amber-50 p-4 rounded mb-4">
                  <span className="text-sm text-amber-800">Unassigned Slips: </span>
                  <span className="font-bold text-amber-800">{reportData.unassigned_slips || 0}</span>
                </div>
                <p className="text-sm text-[#5A5F6E] mb-4">{reportData.total_pickers_with_work} pickers with active work</p>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Picker</th>
                        <th className="px-3 py-2 text-right">Slips</th>
                        <th className="px-3 py-2 text-right">Items</th>
                        <th className="px-3 py-2 text-right">Total Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.picker_workload?.map((p, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2">{p.picker_name}</td>
                          <td className="px-3 py-2 text-right">{p.slip_count}</td>
                          <td className="px-3 py-2 text-right">{p.total_items}</td>
                          <td className="px-3 py-2 text-right">{p.total_qty?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Slip Detail Dialog */}
      <Dialog open={slipDetailOpen} onOpenChange={setSlipDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pick Slip Details</DialogTitle>
          </DialogHeader>
          {selectedSlipDetail && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#5A5F6E]">Slip Number</Label>
                  <p className="font-mono font-medium">{selectedSlipDetail.pick_slip_number}</p>
                </div>
                <div>
                  <Label className="text-[#5A5F6E]">Customer</Label>
                  <p>{selectedSlipDetail.customer_name} ({selectedSlipDetail.customer_code})</p>
                </div>
                <div>
                  <Label className="text-[#5A5F6E]">Generated</Label>
                  <p>{new Date(selectedSlipDetail.generated_at).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-[#5A5F6E]">Status</Label>
                  <p>{getStatusBadge(selectedSlipDetail.status)}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-[#5A5F6E]">Source Orders</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedSlipDetail.source_orders?.map((order, idx) => (
                    <Badge key={idx} variant="outline">{order}</Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-[#5A5F6E]">Items ({selectedSlipDetail.items?.length})</Label>
                <div className="max-h-48 overflow-y-auto border rounded mt-2">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Barcode</th>
                        <th className="px-3 py-2 text-left">Design</th>
                        <th className="px-3 py-2 text-right">Required</th>
                        <th className="px-3 py-2 text-right">In Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSlipDetail.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 font-mono">{item.barcode}</td>
                          <td className="px-3 py-2">{item.design_name}</td>
                          <td className="px-3 py-2 text-right">{item.required_qty}</td>
                          <td className="px-3 py-2 text-right">
                            <span className={item.current_stock < item.required_qty ? 'text-red-600' : 'text-green-600'}>
                              {item.current_stock || 0}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedSlipDetail.items_no_stock?.length > 0 && (
                <div>
                  <Label className="text-amber-600">Excluded (No Stock)</Label>
                  <div className="max-h-32 overflow-y-auto border border-amber-200 bg-amber-50 rounded mt-2 p-2">
                    {selectedSlipDetail.items_no_stock.map((item, idx) => (
                      <div key={idx} className="text-sm py-1">
                        <span className="font-mono">{item.barcode}</span> - {item.design_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded">
                <div>
                  <Label className="text-[#5A5F6E]">Total Items</Label>
                  <p className="text-xl font-bold">{selectedSlipDetail.total_items}</p>
                </div>
                <div>
                  <Label className="text-[#5A5F6E]">Total Qty</Label>
                  <p className="text-xl font-bold">{selectedSlipDetail.total_qty?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-[#5A5F6E]">Total Value</Label>
                  <p className="text-xl font-bold">₹{(selectedSlipDetail.total_value || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Create Dialog */}
      <Dialog open={manualCreateOpen} onOpenChange={setManualCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Manual Pick Slip</DialogTitle>
            <DialogDescription>
              Create a pick slip from specific orders, bypassing auto-generation rules.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Customer Code</Label>
              <Input
                value={manualCustomerCode}
                onChange={(e) => setManualCustomerCode(e.target.value)}
                placeholder="e.g., CUST001"
                className="mt-2"
                data-testid="manual-customer-code"
              />
            </div>
            <div>
              <Label>Order Numbers (comma separated)</Label>
              <Input
                value={manualOrderNumbers}
                onChange={(e) => setManualOrderNumbers(e.target.value)}
                placeholder="e.g., SO-001, SO-002"
                className="mt-2"
                data-testid="manual-order-numbers"
              />
            </div>
            <div>
              <Label>Remarks (optional)</Label>
              <Input
                value={manualRemarks}
                onChange={(e) => setManualRemarks(e.target.value)}
                placeholder="Any special notes..."
                className="mt-2"
                data-testid="manual-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleManualCreate}
              disabled={manualCreating || !manualCustomerCode || !manualOrderNumbers}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="manual-create-submit"
            >
              {manualCreating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Slip
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderManagementDashboard;
